import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Box, Col } from '@opendoor/atoms/box';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import { motion } from 'framer-motion';

import type { MegaNavItem } from '../../types';

interface GlassMegaNavFlyoutProps {
  item: MegaNavItem | null;
}

const flyoutVariants = {
  hidden: {
    opacity: 0,
    y: -8,
    transition: { duration: 0.2, ease: 'easeOut' },
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.2, ease: 'easeOut' },
  },
};

const GlassMegaNavFlyout: React.FC<GlassMegaNavFlyoutProps> = ({ item }) => {
  const isVisible = item !== null;
  const [isAnimationComplete, setIsAnimationComplete] = useState<boolean>(false);
  const flyoutRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isVisible) {
      setIsAnimationComplete(false);
    }
  }, [isVisible]);

  const handleAnimationComplete = useCallback((definition: string) => {
    if (definition === 'visible') {
      setIsAnimationComplete(true);
      const firstLink = flyoutRef.current?.querySelector('a');
      firstLink?.focus();
    }
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    const links = flyoutRef.current?.querySelectorAll('a');
    if (!links?.length) {
      return;
    }

    const currentIndex = Array.from(links).indexOf(document.activeElement as HTMLAnchorElement);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = currentIndex < links.length - 1 ? currentIndex + 1 : 0;
      links[next].focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = currentIndex > 0 ? currentIndex - 1 : links.length - 1;
      links[prev].focus();
    }
  }, []);

  // Static column class per section count (1–4) so the grid needs no inline style.
  const sectionCount = Math.min(Math.max(item?.sections?.length ?? 1, 1), 4);
  const gridColsClass = { 1: 'grid-cols-1', 2: 'grid-cols-2', 3: 'grid-cols-3', 4: 'grid-cols-4' }[
    sectionCount
  ];

  return (
    <motion.div
      ref={flyoutRef}
      initial="hidden"
      animate={isVisible ? 'visible' : 'hidden'}
      variants={flyoutVariants}
      onAnimationComplete={handleAnimationComplete}
      onKeyDown={handleKeyDown}
      role="menu"
      aria-label={item ? `${item.name} submenu` : undefined}
      // top-18 mirrors the GLASS_HEADER_HEIGHT (72px) header; z-[1000] sits below the header (z-2000).
      className={cn(
        'fixed inset-x-0 top-18 z-[1000] border-b border-b-od-content-inverse-primary/8',
        isVisible ? 'visible' : 'invisible',
      )}
    >
      <Box
        className={cn(
          'w-full justify-center bg-od-bg-scrim',
          isVisible && isAnimationComplete ? 'pointer-events-auto' : 'pointer-events-none',
        )}
      >
        <Box className={cn('grid w-full max-w-328 gap-45 px-5 pt-10 pb-11', gridColsClass)}>
          {item?.sections?.map((section, sectionIndex) => (
            <Col key={sectionIndex} className="gap-5">
              <Text as="p" className="block text-label-md text-od-content-inactive-accent-brand">
                {section.title}
              </Text>
              <Col className="gap-2">
                {section.links.map((link, linkIndex) => (
                  <Box
                    key={linkIndex}
                    href={link.href}
                    role="menuitem"
                    aria-label={`Navigate to ${link.name}`}
                    analyticsName={`cosmos-glass-nav-${item.name.toLowerCase()}-${link.name
                      .toLowerCase()
                      .replace(/\s+/g, '-')}`}
                    className="group block no-underline transition-colors duration-150"
                  >
                    <Text
                      as="span"
                      className="text-body-md text-od-content-inverse-primary opacity-75 transition-opacity duration-150 group-hover:opacity-100"
                    >
                      {link.name}
                    </Text>
                  </Box>
                ))}
              </Col>
            </Col>
          ))}
        </Box>
      </Box>
    </motion.div>
  );
};

export default GlassMegaNavFlyout;
